import { useQuery } from "@tanstack/react-query";
import { Modal, Popover, Spin, Table } from "antd";
import {
  handleFetchTopCreditors,
  handleFetchUniqueAnalysis,
  handleFetchUniqueBusiness,
  handleFetchUniqueCategories,
} from "../../../../api/customers";
import formatMoney from "../../../../utils/formatMoney";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../../contexts/AuthContext";
import { GoChevronDown } from "react-icons/go";
import { MdCheck } from "react-icons/md";

export default function CreditorsList({
  isVisible,
  setVisible,
  businessId,
  companyNo,
}: any) {
  const [catGroup, setCatGroup] = useState<any>("");
  const [busGroup, setBusGroup] = useState<any>("");
  const [anaGroup, setAnaGroup] = useState<any>("");
  const [extGroup, setExtGroup] = useState<any>("");
  const [busId, setBusId] = useState<any>(businessId);
  const [change, setChange] = useState<boolean>(false);
  const { auth } = useContext(AuthContext);
  const access = [4, 5, 9];

  const [pagination, setPagination] = useState<any>({
    page: 1,
    limit: 10,
    total: 0,
  });

  const params = `categories=${catGroup}&business=${busGroup}&analysis=${anaGroup}&external=${extGroup}`;
  const { data: customers, isLoading } = useQuery({
    queryKey: ["allmetrics", pagination.page, pagination.limit, busId, params],
    queryFn: () =>
      handleFetchTopCreditors({
        page: pagination.page,
        limit: pagination.limit,
        businessId: busId,
        params,
      }),
  });

  const { data: businesses } = useQuery({
    queryKey: ["companies"],
    queryFn: () => handleFetchUniqueBusiness(),
  });

  const { data: categories } = useQuery({
    queryKey: ["categories"],
    queryFn: () => handleFetchUniqueCategories(),
  });

  const { data: analysis } = useQuery({
    queryKey: ["analysis"],
    queryFn: () => handleFetchUniqueAnalysis(),
  });

  const external = [{ value: "Y" }, { value: "N" }];

  const handlePageChange = (e: any) => {
    setPagination((prev: any) => ({
      ...prev,
      page: e.current,
      limit: e.pageSize,
    }));
  };

  const LabelDisplay = ({ data }: any) => {
    let displayText: string;

    if (data?.length === 0) {
      displayText = "All";
    } else if (data.length === 1) {
      displayText = data[0];
    } else {
      displayText = "Multiple";
    }

    return <div className="">{displayText}</div>;
  };

  const getColumns = (auth: any, access: any) => {
    const columns: any = [
      {
        title: "RANK",
        width: 40,
        align: "center",
        render: (record: any, text: any, index: any) => (
          <span className="text-gray-700 ml-2">
            {pagination?.page * pagination?.limit -
              pagination?.limit +
              index +
              1}
          </span>
        ),
      },
      {
        title: "ACCOUNT",
        width: 80,
        align: "center",
        render: (record: any) => (
          <span className="text-gray-700">{record?.account || "-"}</span>
        ),
      },
      {
        title: "CUSTOMER",
        width: 260,
        render: (record: any) => (
          <div className="text-gray-700">
            <div className="truncate">{record?.name || "-"}</div>
          </div>
        ),
      },

      {
        title: "CREDIT LIMIT",
        width: 100,
        align: "center",
        render: (record: any) =>
          access.includes(auth?.accessLevel) ? (
            <div className="text-gray-700 text-center">
              {formatMoney(record?.currCredit)}
            </div>
          ) : null,
      },
    ];

    return columns.filter((column: any) => {
      if (
        column.title === "O/BALANCE" ||
        column.title === "O/I" ||
        column.title === "CREDIT LIMIT" ||
        column.title === "YTD TURNOVER" ||
        column.title === "PREV TURNOVER"
      ) {
        return access.includes(auth?.accessLevel);
      }
      return true;
    });
  };

  const columns = getColumns(auth, access);

  const handleAddItem = (type: string, value: any) => {
    switch (type) {
      case "business":
        setBusId("");
        setChange(true);
        setBusGroup((prevComps: any) =>
          prevComps.includes(value)
            ? prevComps.filter((item: any) => item !== value)
            : [...prevComps, value]
        );
        break;
      case "category":
        setCatGroup((prevCats: any) =>
          prevCats.includes(value)
            ? prevCats.filter((item: any) => item !== value)
            : [...prevCats, value]
        );
        break;
      case "analysis":
        setAnaGroup((prevAnaly: any) =>
          prevAnaly.includes(value)
            ? prevAnaly.filter((item: any) => item !== value)
            : [...prevAnaly, value]
        );
        break;
      case "external":
        setExtGroup((prevExtn: any) =>
          prevExtn.includes(value)
            ? prevExtn.filter((item: any) => item !== value)
            : [...prevExtn, value]
        );
        break;
      default:
        console.warn("Unknown type:", type);
        break;
    }
  };

  const busOptions = (
    <div>
      <button
        onClick={() => {
          setBusId("");
          setChange(true);
          setBusGroup("");
        }}
        className="border-t-4 py-1 hover:bg-gray-100 cursor-pointer w-full flex justify-between"
      >
        <div className="">( Select All )</div>
        <div
          className={`w-6 h-6 flex justify-center items-center border border-gray-400 ${
            busGroup === "" ? "bg-green-500" : ""
          }`}
        >
          {busGroup === "" ? <MdCheck color="#FFFFFF" /> : null}
        </div>
      </button>
      {businesses?.map((item: any, index: number) => (
        <button
          onClick={() => handleAddItem("business", item?.value)}
          key={index}
          className="border-t py-1 hover:bg-gray-100 cursor-pointer w-full flex justify-between"
        >
          <div className="">{item?.name}</div>
          <div
            className={`w-6 h-6 flex justify-center items-center border border-gray-400 ${
              busGroup?.includes(item?.value)
                ? "bg-green-500"
                : busGroup === ""
                ? "bg-green-500"
                : ""
            }`}
          >
            {busGroup?.includes(item?.value) ? (
              <MdCheck color="#FFFFFF" />
            ) : busGroup === "" ? (
              <MdCheck color="#FFFFFF" />
            ) : null}
          </div>
        </button>
      ))}
    </div>
  );

  const catOptions = (
    <div>
      <button
        onClick={() => setCatGroup("")}
        className="border-t-4 py-1 hover:bg-gray-100 cursor-pointer w-full flex justify-between"
      >
        <div className="">( Select All )</div>
        <div
          className={`w-6 h-6 flex justify-center items-center border border-gray-400 ${
            catGroup === "" ? "bg-green-500" : ""
          }`}
        >
          {catGroup === "" ? <MdCheck color="#FFFFFF" /> : null}
        </div>
      </button>
      {categories?.map((item: any, index: number) => (
        <button
          onClick={() => handleAddItem("category", item?.value)}
          key={index}
          className="border-t py-1 hover:bg-gray-100 cursor-pointer w-full flex justify-between"
        >
          <div className="">{item?.value}</div>
          <div
            className={`w-6 h-6 flex justify-center items-center border border-gray-400 ${
              catGroup?.includes(item?.value)
                ? "bg-green-500"
                : catGroup === ""
                ? "bg-green-500"
                : ""
            }`}
          >
            {catGroup?.includes(item?.value) ? (
              <MdCheck color="#FFFFFF" />
            ) : catGroup === "" ? (
              <MdCheck color="#FFFFFF" />
            ) : null}
          </div>
        </button>
      ))}
    </div>
  );

  const extOptions = (
    <div>
      <button
        onClick={() => setExtGroup("")}
        className="border-t-4 py-1 hover:bg-gray-100 cursor-pointer w-full flex justify-between"
      >
        <div className="">( Select All )</div>
        <div
          className={`w-6 h-6 flex justify-center items-center border border-gray-400 ${
            extGroup === "" ? "bg-green-500" : ""
          }`}
        >
          {extGroup === "" ? <MdCheck color="#FFFFFF" /> : null}
        </div>
      </button>
      {external?.map((item: any, index: number) => (
        <button
          onClick={() => handleAddItem("external", item?.value)}
          key={index}
          className="border-t py-1 hover:bg-gray-100 cursor-pointer w-full flex justify-between"
        >
          <div className="">{item?.value}</div>
          <div
            className={`w-6 h-6 flex justify-center items-center border border-gray-400 ${
              extGroup?.includes(item?.value)
                ? "bg-green-500"
                : extGroup === ""
                ? "bg-green-500"
                : ""
            }`}
          >
            {extGroup?.includes(item?.value) ? (
              <MdCheck color="#FFFFFF" />
            ) : extGroup === "" ? (
              <MdCheck color="#FFFFFF" />
            ) : null}
          </div>
        </button>
      ))}
    </div>
  );

  const anaOptions = (
    <div>
      <button
        onClick={() => setAnaGroup("")}
        className="border-t-4 py-1 hover:bg-gray-100 cursor-pointer w-full flex justify-between"
      >
        <div className="">( Select All )</div>
        <div
          className={`w-6 h-6 flex justify-center items-center border border-gray-400 ${
            anaGroup === "" ? "bg-green-500" : ""
          }`}
        >
          {anaGroup === "" ? <MdCheck color="#FFFFFF" /> : null}
        </div>
      </button>
      {analysis?.map((item: any, index: number) => (
        <button
          onClick={() => handleAddItem("analysis", item?.value)}
          key={index}
          className="border-t py-1 hover:bg-gray-100 cursor-pointer w-full flex justify-between"
        >
          <div className="">{item?.value}</div>
          <div
            className={`w-6 h-6 flex justify-center items-center border border-gray-400 ${
              anaGroup?.includes(item?.value)
                ? "bg-green-500"
                : anaGroup === ""
                ? "bg-green-500"
                : ""
            }`}
          >
            {anaGroup?.includes(item?.value) ? (
              <MdCheck color="#FFFFFF" />
            ) : anaGroup === "" ? (
              <MdCheck color="#FFFFFF" />
            ) : null}
          </div>
        </button>
      ))}
    </div>
  );

  useEffect(() => {
    if (!change) {
      setBusId(businessId);
      setBusGroup([companyNo]);
    }
  }, [companyNo]);

  useEffect(() => {
    setPagination({ ...pagination, total: customers?.total });
  }, [customers]);

  return (
    <Modal
      open={isVisible}
      title="Credit Limit"
      onCancel={() => setVisible(false)}
      width={1200}
      footer={null}
      className="text-xxs"
    >
      <div className="mb-4">
        <div className="flex justify-between bg-gray-100 border p-2">
          <div className="">Company</div>
          <div className="">
            <Popover
              placement="bottomRight"
              title={"Select Company"}
              content={busOptions}
            >
              <div className="flex items-center cursor-pointer text-xxs">
                <div className="pl-2 hidden md:block border-l uppercase">
                  <LabelDisplay data={busGroup} />
                </div>
                <div className="">
                  <GoChevronDown color="#323232" size={20} />
                </div>
              </div>
            </Popover>
          </div>
        </div>
        <div className="flex justify-between bg-gray-100 border p-2">
          <div className="">Category</div>
          <div className="">
            <Popover
              placement="bottomRight"
              title={"Select Category"}
              content={catOptions}
            >
              <div className="flex items-center cursor-pointer text-xxs">
                <div className="pl-2 hidden md:block border-l uppercase">
                  <LabelDisplay data={catGroup} />
                </div>
                <div className="">
                  <GoChevronDown color="#323232" size={20} />
                </div>
              </div>
            </Popover>
          </div>
        </div>

        <div className="flex justify-between bg-gray-100 border p-2">
          <div className="">External</div>
          <div className="">
            <Popover
              placement="bottomRight"
              title={"Select Type"}
              content={extOptions}
            >
              <div className="flex items-center cursor-pointer text-xxs">
                <div className="pl-2 hidden md:block border-l uppercase">
                  <LabelDisplay data={extGroup} />
                </div>
                <div className="">
                  <GoChevronDown color="#323232" size={20} />
                </div>
              </div>
            </Popover>
          </div>
        </div>
        <div className="flex justify-between bg-gray-100 border p-2">
          <div className="">Analysis</div>
          <div className="">
            <Popover
              placement="bottomRight"
              title={"Select Analysis"}
              content={anaOptions}
            >
              <div className="flex items-center cursor-pointer text-xxs">
                <div className="pl-2 hidden md:block border-l uppercase">
                  <LabelDisplay data={anaGroup} />
                </div>
                <div className="">
                  <GoChevronDown color="#323232" size={20} />
                </div>
              </div>
            </Popover>
          </div>
        </div>
      </div>
      <Spin spinning={isLoading}>
        <Table
          size="small"
          dataSource={customers?.records}
          columns={columns}
          scroll={{ y: 660, x: 0 }}
          rowKey="id"
          style={{ fontSize: "12px" }}
          bordered
          pagination={{
            current: pagination?.page,
            pageSize: pagination?.limit,
            total: pagination?.total,
          }}
          onChange={(e: any) => handlePageChange(e)}
          loading={isLoading}
          locale={{
            emptyText: "No data available",
          }}
        />
      </Spin>
    </Modal>
  );
}
